import { useQueryClient } from '@tanstack/react-query';
import { formatISO } from 'date-fns';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';

import { HomeWidgetEmptyState } from '@/modules/home';
import { useGetInventoryTransactions } from '@/modules/home/apiHooks';
import { useHomeInputsUtils } from '@/modules/home/components/PageContent/Inputs/logic';
import { useHomePageContext } from '@/modules/home/context';
import { IHome } from '@/modules/home/types';
import AddEditWarehouse from '@/modules/settings/property/components/Warehouses/AddEditWarehouse';
import { useGetWarehouses } from '@/modules/warehouseManagement/shared/apiHooks';
import { Button, Table, Typography } from '@/shared/components/atoms';
import routes from '@/shared/constant/routes';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

import Convert3DCube from '~/images/shared/Convert3DCube.svg';

const TransactionsTable = () => {
  const { t } = useTranslation();

  const [selectedAsset] = useStore((store) => store?.asset);

  const { openModal } = useModal();

  const {
    state: { date },
  } = useHomePageContext();

  const {
    data: response,
    fetchNextPage,
    hasNextPage,
    isSuccess,
  } = useGetInventoryTransactions({
    entityId: selectedAsset?.id,
    date: formatISO(date, { representation: 'date' }),
    page: 1,
    limit: 10,
    isInventoryActive: selectedAsset?.IsInventoryActive,
  });

  const { data: entityWarehouses, isLoading: isWarehousesLoading } =
    useGetWarehouses({
      page: 1,
      limit: 10,
      entityId: selectedAsset?.id,
      isInventoryActive: Boolean(selectedAsset?.IsInventoryActive),
      paginated: true,
    });

  const data = response?.pages
    ?.filter((page) => page?.items)
    .flatMap((page) => page?.items);

  const { getTransactionsRequestsTableColumns } = useHomeInputsUtils();

  const queryClient = useQueryClient();

  const onAddWarehouseSuccess = () => {
    queryClient.invalidateQueries(['getInventoryTransactions']);
  };

  return data?.length ? (
    <Table<IHome.IInventoryTransactions>
      data={data}
      columns={isSuccess ? getTransactionsRequestsTableColumns() : []}
      selection={false}
      pagination={false}
      headCellClassName='text-start  [&>div]:text-14 border-b-xs border-neutralVariant-90 text-surface-10'
      rowCellClassName='border-b-xs border-neutralVariant-90'
      headContainerClassName='!bg-background-neutral-99'
      rowContainerClassName='!bg-background-transparent'
      tableWrapperClassName='max-h-343'
      headerClassName='!z-[1]'
      infiniteScrollProps={{
        loadMore: () => fetchNextPage(),
        hasMore: hasNextPage,
      }}
    />
  ) : !data?.length &&
    !(selectedAsset?.IsInventoryActive && isWarehousesLoading) ? (
    <HomeWidgetEmptyState
      icon={<Convert3DCube width='48' height='48' />}
      title={t('No Transactions!')}
      message={
        !selectedAsset?.IsInventoryActive ? (
          t('entity-inventory-inactive')
        ) : entityWarehouses?.totalItems ? (
          <Link
            className='mt-16 flex h-40 items-center justify-center rounded-lg border-xs border-outline-default px-16 text-primary-default'
            href={routes.warehouses}
          >
            {t('add-inbound-delivery')}
          </Link>
        ) : (
          <Button
            size='none'
            variant='text'
            className='mt-16 flex h-40 items-center justify-center border-xs border-outline-default px-16'
            onClick={() => {
              openModal(
                <AddEditWarehouse
                  property={selectedAsset ?? undefined}
                  onSuccess={onAddWarehouseSuccess}
                />,
                {
                  variant: 'full',
                  title: (
                    <div>
                      <Typography weight='bold'>
                        {t('add-warehouse')}
                      </Typography>
                      <Typography
                        variant='subtitle2'
                        className='mt-4 text-surface-10'
                        weight='bold'
                      >{`${t('property')}: ${selectedAsset?.name}`}</Typography>
                    </div>
                  ),
                }
              );
            }}
          >
            {t('add-new-warehouse')}
          </Button>
        )
      }
    />
  ) : null;
};

export { TransactionsTable };
